import React, { useState, useEffect } from 'react';
import { useUser } from 'util/UserContext';
import api from 'config/axiosConfig';
import { Info, ArrowDownUp } from 'lucide-react';
import Modal from 'ui/Modal';
import { Impact, Influence, Issue } from 'util/constants';
import AddImpact from 'components/Impacts/AddImpact';
import IssueSearch from 'components/Issues/IssueSearch';
import { parseWikipediaData } from 'util/wikiUtils';

interface Props {
  currentIssue?: IssueSuggestion | null | undefined;
  onInfluenceAdded: (influence: Influence) => void;
  open: boolean;
  onClose: () => void;
  excludeIssueIds: number[];
}

interface IssueSuggestion extends Issue {
  type?: 'issue';
}

type Suggestion =
  | {
      type: 'issue';
      id: number;
      name: string;
      cname: string;
      subject_properties?: any;
    }
  | {
      type: 'wiki';
      id?: number;
      name: string;
      link: string;
      cname: string;
      subject_properties?: any;
    };

const AddInfluence: React.FC<Props> = ({
  currentIssue,
  onInfluenceAdded,
  open,
  onClose,
  excludeIssueIds,
}) => {
  const { currentUser, setShowAuthModal } = useUser();

  // Separate state variables for influencer and influenced search queries and selections
  const [influencerSearchQuery, setInfluencerSearchQuery] = useState<string>('');
  const [selectedInfluencerIssue, setSelectedInfluencerIssue] = useState<Suggestion | IssueSuggestion | null>(null);

  const [influencedSearchQuery, setInfluencedSearchQuery] = useState<string>('');
  const [selectedInfluencedIssue, setSelectedInfluencedIssue] = useState<Suggestion | IssueSuggestion | null>(
    currentIssue ? currentIssue : null
  );

  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [searchValidationMessage, setSearchValidationMessage] = useState<string>('');
  const [submitEnabled, setSubmitEnabled] = useState<boolean>(false);
  const [showAssessmentModal, setShowAssessmentModal] = useState<boolean>(false);
  const [newInfluence, setNewInfluence] = useState<Influence | null>(null);

  useEffect(() => {
    const isValidSubmit = () => {
      if (!selectedInfluencerIssue && influencerSearchQuery.trim().length > 0) {
        setSearchValidationMessage('Please select an influencer.');
        return false;
      }
      if (!selectedInfluencedIssue && influencedSearchQuery.trim().length > 0) {
        setSearchValidationMessage('Please select an influenced issue.');
        return false;
      }
      if (!selectedInfluencerIssue || !selectedInfluencedIssue) {
        return false;
      }
      if (isSubmitting) {
        return false;
      }
      setSearchValidationMessage('');
      setError('');
      return true;
    };

    setSubmitEnabled(isValidSubmit());
  }, [
    selectedInfluencerIssue,
    selectedInfluencedIssue,
    isSubmitting,
    influencerSearchQuery,
    influencedSearchQuery,
  ]);

  const uploadReferenceImage = async (imageUrl: string, issueId: number) => {
    if (!imageUrl) return null;
    try {
      const imageResponse = await fetch(imageUrl);
      if (!imageResponse.ok) return null;
      
      let contentType = imageResponse.headers.get('content-type');

      if (!contentType) {
        contentType = 'image/jpeg';
      }
      if (contentType.includes('image/svg+xml')) {
        contentType = 'image/svg';
      }
      if (contentType.includes('image/jpeg')) {
        contentType = 'image/jpg';
      }

      const blob = await imageResponse.blob();
      if (blob) {
        try {
          const formData = new FormData();
          formData.append(
            'image',
            blob,
            `reference_image.${contentType?.split('/')[1].replace('jpeg', 'jpg')}`
          );
          const response = await api.post(`/api/issues/image/${issueId}`, formData);
          return response.data.url;
        } catch (err) {
          console.error('Error uploading reference image:', err);
          return null;
        }
      }
    } catch (err) {
      console.error('Error fetching image:', err);
      return null;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentUser) {
      setShowAuthModal(true);
      return;
    }
    if (!submitEnabled || !selectedInfluencerIssue || !selectedInfluencedIssue) {
      return;
    }
    setIsSubmitting(true);
    setError('');
    setSuccess('');

    try {
      // Process influencer issue conversion if needed
      let influencerIssueToLink = selectedInfluencerIssue;
      if (influencerIssueToLink && influencerIssueToLink.type === 'wiki') {
        const wikiImg = await parseWikipediaData(influencerIssueToLink.cname, true);
        try {
          const response = await api.post(`/api/issues`, {
            subject_cname: influencerIssueToLink.cname,
            subject_properties: {
              ...influencerIssueToLink.subject_properties,
              wiki_parse_first_img: wikiImg.wiki_parse_first_img,
            },
            wikipedia_link: influencerIssueToLink.link,
          });
          if (response.data.properties.subject_properties.wiki_parse_first_img) {
            await uploadReferenceImage(
              response.data.properties.subject_properties.wiki_parse_first_img,
              response.data.id
            );
          }
          influencerIssueToLink = { ...response.data, type: 'issue' as const };
          setSelectedInfluencerIssue(influencerIssueToLink);
        } catch (err: any) {
          setError('Failed to create influencer issue. Please try again.');
          setIsSubmitting(false);
          return;
        }
      }

      // Process influenced issue conversion if needed
      let influencedIssueToLink = selectedInfluencedIssue;
      if (influencedIssueToLink && influencedIssueToLink.type === 'wiki') {
        const wikiImg = await parseWikipediaData(influencedIssueToLink.cname, true);
        try {
          const response = await api.post(`/api/issues`, {
            subject_cname: influencedIssueToLink.cname,
            subject_properties: {
              ...influencedIssueToLink.subject_properties,
              wiki_parse_first_img: wikiImg.wiki_parse_first_img,
            },
            wikipedia_link: influencedIssueToLink.link,
          });
          if (response.data.properties.subject_properties.wiki_parse_first_img) {
            await uploadReferenceImage(
              response.data.properties.subject_properties.wiki_parse_first_img,
              response.data.id
            );
          }
          influencedIssueToLink = { ...response.data, type: 'issue' as const };
          setSelectedInfluencedIssue(influencedIssueToLink);
        } catch (err: any) {
          setError('Failed to create influenced issue. Please try again.');
          setIsSubmitting(false);
          return;
        }
      }

      // Create the influence relationship with the processed issue IDs
      const response = await api.post(
        `/api/influences/${influencerIssueToLink.id}/${influencedIssueToLink.id}`
      );
      const newInfluenceData = response.data;
      await api.post(`/api/votes/influence/${newInfluenceData.id}`, {
        user_id: currentUser.id,
        rel_type: 'user_upvote',
      });
      setSuccess('Influence added successfully!');
      onInfluenceAdded({ ...newInfluenceData, user_vote: 'user_upvote', vote_count: 1 });
      setNewInfluence(newInfluenceData);
      setShowAssessmentModal(true);
    } catch (err: any) {
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Failed to create influence relationship. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInfluencerIssueSelect = (suggestion: Suggestion) => {
    setSelectedInfluencerIssue(suggestion);
    setInfluencerSearchQuery(suggestion.name);
  };

  const handleInfluencerSearchChange = () => {
    setSelectedInfluencerIssue(null);
    setInfluencerSearchQuery('');
  };

  const handleInfluencedIssueSelect = (suggestion: Suggestion) => {
    setSelectedInfluencedIssue(suggestion);
    setInfluencedSearchQuery(suggestion.name);
  };

  const handleInfluencedSearchChange = () => {
    setSelectedInfluencedIssue(null);
    setInfluencedSearchQuery('');
  };

  // Swap the two issues—even if one is null.
  const handleSwapIssues = (e: React.MouseEvent) => {
    e.preventDefault();
    const newInfluencer = selectedInfluencedIssue;
    const newInfluenced = selectedInfluencerIssue;
    setSelectedInfluencerIssue(newInfluencer);
    setSelectedInfluencedIssue(newInfluenced);
    setInfluencerSearchQuery(newInfluencer ? newInfluencer.name : '');
    setInfluencedSearchQuery(newInfluenced ? newInfluenced.name : '');
  };

  const handleInfluencerIssueClose = () => {
    setSelectedInfluencerIssue(null);
    setInfluencerSearchQuery('');
  };

  const handleInfluencedIssueClose = () => {
    setSelectedInfluencedIssue(null);
    setInfluencedSearchQuery('');
  };

  const handleImpactAdded = (impact: Impact) => {
    setShowAssessmentModal(false);
    setNewInfluence(null);
    handleCloseModal();
  };

  const handleCloseModal = () => {
    onClose();
    setInfluencerSearchQuery('');
    setInfluencedSearchQuery('');
    setSelectedInfluencerIssue(null);
    setSelectedInfluencedIssue(null);
    setError('');
    setSuccess('');
    setSearchValidationMessage('');
  };

  return (
    <>
      {showAssessmentModal !== true && (
        <Modal
          isOpen={open}
          onClose={handleCloseModal}
          title={`${currentIssue ? 'Add influencing issue' : 'Add influence'}`}
        >
          <div className="flex flex-col h-[calc(100%-44px)] max-h-[calc(100%-44px)] sm:h-auto overflow-y-scroll sm:overflow-y-auto space-y-4">
            <div className="flex flex-col flex-grow space-y-4">
              <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                {currentIssue
                  ? 'Link an issue (influencer) that influences this issue (influenced).'
                  : 'Link an issue (influencer) to another issue (influenced) as an influence.'}
              </p>

              <form className="flex flex-col flex-grow space-y-4">
                <div className="form-control mb-0">
                  <IssueSearch
                    inputType="add"
                    placeholder="Influencer"
                    excludeIssueIds={[currentIssue?.id, ...excludeIssueIds]}
                    className="w-full"
                    fieldRequired={true}
                    prepopulateData={selectedInfluencerIssue as Issue}
                    validationMessage={searchValidationMessage}
                    validationState={
                      searchValidationMessage && influencerSearchQuery.trim() === ''
                        ? 'invalid'
                        : selectedInfluencerIssue
                        ? 'valid'
                        : 'none'
                    }
                    onSelect={handleInfluencerIssueSelect}
                    onSearchChange={handleInfluencerSearchChange}
                    onEntityChipClose={handleInfluencerIssueClose}
                  />
                </div>
                <button
                  type="button"
                  className="flex items-center justify-center rounded-full btn-primary py-1"
                  onClick={handleSwapIssues}
                >
                  <ArrowDownUp className="size-6" />
                </button>
                <div className="form-control">
                  <IssueSearch
                    inputType="add"
                    placeholder="Influenced"
                    excludeIssueIds={excludeIssueIds}
                    className="w-full"
                    fieldRequired={true}
                    prepopulateData={selectedInfluencedIssue as Issue}
                    validationMessage={searchValidationMessage}
                    validationState={
                      searchValidationMessage && influencedSearchQuery.trim() === ''
                        ? 'invalid'
                        : selectedInfluencedIssue
                        ? 'valid'
                        : 'none'
                    }
                    onSelect={handleInfluencedIssueSelect}
                    onSearchChange={handleInfluencedSearchChange}
                    onEntityChipClose={handleInfluencedIssueClose}
                  />
                </div>

                {(error || success) && (
                  <div className="flex items-center space-x-2">
                    {error && (
                      <div className="text-warning-red dark:text-warning-red-dark text-sm flex items-center">
                        <Info className="mr-1 size-4" />
                        <span>{error}</span>
                      </div>
                    )}
                    {success && (
                      <div className="flex items-center text-green-600 text-sm">
                        <Info className="mr-1 size-4" />
                        <span>{success}</span>
                      </div>
                    )}
                  </div>
                )}
              </form>
            </div>

            <div className="flex flex-grow flex-col pb-8 justify-end place-items-end mb-6 mt-auto space-y-2 sm:space-y-0 sm:space-x-2 sm:flex sm:justify-end sm:flex-row sm:mb-0">
              <button
                type="button"
                onClick={handleCloseModal}
                className="btn-secondary w-full sm:w-fit py-2 px-4"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!submitEnabled}
                onClick={handleSubmit}
                className={`w-full sm:w-fit py-2 px-4 ${
                  !submitEnabled ? 'btn-primary-disabled' : 'btn-primary'
                } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {isSubmitting ? 'Continuing...' : 'Continue'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {newInfluence && (
        <AddImpact
          entityType="influence"
          entity={newInfluence}
          onImpactAdded={handleImpactAdded}
          open={showAssessmentModal}
          onClose={() => {
            setShowAssessmentModal(false);
            handleCloseModal();
          }}
        />
      )}
    </>
  );
};

export default AddInfluence;
